import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import { MCard } from "components/Shared";
import { getInspectorCaseTime, setInspectorCaseTime } from "requests/inspectorTimeCard/index.ts";
import { DATA_TYPE_NUMBER, DATA_TYPE_TEXT_BUTTON } from "typeConstants/dataTypes";
import { formatDecimalPercent } from "components/Shared/Formatters";
import MGrid, { ACTION_SAVE, ACTION_DOWNLOAD, FILTER_TYPE_NONE, COL_CODE_CONTROL_NUMBER, FORMATTER_RIGHT_ALIGN } from "components/Shared/MGrid.tsx";
import { filterOnFilterDate } from "components/Homeowner/History/DataFormatters";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const ExpectedMinutesFormatter = ({ row }) => (
  <div title={row.expectedMinutesFormatterTipText} style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
    {row.expectedMinutes}
  </div>
);
ExpectedMinutesFormatter.propTypes = { row: PropTypes.object };

const Performance = ({ inspectorNumber, payPeriodId, payPeriodClosed, makeToast, onControlNumberSelected, filterDate }) => {
  const mounted = useRef(true);
  const [inspectorPerformance, setInspectorPerformance] = useState(null);
  const [editDialogBoxVisible, setEditDialogBoxVisible] = useState(false);
  const [currentlyEditingId, setCurrentlyEditingId] = useState(null);
  const [performanceUpdates, setPerformanceUpdates] = useState([]);

  useEffect(() => () => mounted.current = false, []);

  useEffect(() => {
    setInspectorPerformance(null);
    getInspectorCaseTime(inspectorNumber, payPeriodId, transformAndSetCaseTime, mounted);
  }, [inspectorNumber, payPeriodId]);

  const handleEditRow = (id) => {
    setCurrentlyEditingId(id);
    setEditDialogBoxVisible(true);
  };

  const editDialogBox = () => {
    if (!inspectorPerformance || (inspectorPerformance && !inspectorPerformance.length)) {
      return <div />;
    } else if (!currentlyEditingId) {
      return <div />;
    }

    let fieldToEdit = inspectorPerformance.find(s => s.controlIdNumber === currentlyEditingId);
    return (
      <div className="clockinout-dialog-box">
        <Dialog
          open={editDialogBoxVisible}
          onClose={() => { }}
        >
          <DialogContent sx={{ padding: 0 }}>
            <MCard
              title="Edit Minutes"
              fields={[
                {
                  key: "driveTimeEdit",
                  label: "Drive Time",
                  value: fieldToEdit.driveTime,
                  dataType: DATA_TYPE_NUMBER,
                  validation: yup.number("Numeric Value Required").required("Drive Time is a required field")
                },
                {
                  key: "siteTimeEdit",
                  label: "Site Time",
                  value: fieldToEdit.siteTime,
                  dataType: DATA_TYPE_NUMBER,
                  validation: yup.number("Numeric Value Required").required("Site Time is a required field")
                },
                {
                  key: "webTimeEdit",
                  label: "Web Time",
                  value: fieldToEdit.webTime,
                  dataType: DATA_TYPE_NUMBER,
                  validation: yup.number("Numeric Value Required").required("Site Time is a required field")
                },
              ]}
              startInEditMode={true}
              isEditable={true}
              onEditCancelled={() => {
                setCurrentlyEditingId(null);
                setEditDialogBoxVisible(false);
              }}
              onEditFinished={(arr, obj) => handleEditConfirm(obj)}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const handleEditConfirm = (obj) => {
    let newInspectorPerf = [...inspectorPerformance];
    let newPerfUpdates = [...performanceUpdates];

    let indexToUpdate = newInspectorPerf.findIndex(s => s.controlIdNumber === currentlyEditingId);
    if (indexToUpdate !== -1) {
      if (obj.driveTimeEdit) newInspectorPerf[indexToUpdate].driveTime = obj.driveTimeEdit;
      if (obj.siteTimeEdit) newInspectorPerf[indexToUpdate].siteTime = obj.siteTimeEdit;
      if (obj.webTimeEdit) newInspectorPerf[indexToUpdate].webTime = obj.webTimeEdit;
      newInspectorPerf[indexToUpdate].wasEdited = (
        <div style={{ textAlign: "center" }}>
          <Icon>update</Icon>
        </div>
      );
    }
    let updatedPerfEntry = newInspectorPerf.find(s => s.controlIdNumber === currentlyEditingId);
    newPerfUpdates.push({
      controlIdNumber: currentlyEditingId,
      driveTime: updatedPerfEntry.driveTime,
      siteTime: updatedPerfEntry.siteTime,
      webTime: updatedPerfEntry.webTime
    });
    setCurrentlyEditingId(null);
    setEditDialogBoxVisible(false);
    setPerformanceUpdates(newPerfUpdates);

    // Force Reload
    const reload = new Promise((resolve) => {
      setInspectorPerformance(null);
      resolve();
    });
    reload.then(() => setInspectorPerformance(newInspectorPerf));
  };

  const onCellClick = (row, column) => {
    const rowItem = filteredPerformance[row];
    switch (column) {
      case "controlIdNumber":
        if (onControlNumberSelected) {
          onControlNumberSelected(rowItem.controlIdNumber);
        }
        break;
    }
  };

  const transformAndSetCaseTime = data => {
    // pass in driveTime, siteTime, webTime
    const getToolTip = (...params) => ["Drive", "Site", "Web"].map((l, i) => `${l} Time: ${params[i]} mins`).join("\n");

    data = data.map(d => ({
      controlIdNumber: d.controlIdNumber,
      surveyTypeName: d.surveyTypeName,
      wasRejected: d.wasRejected ? "R" : "",
      driveTime: d.minutesActualDetail.driveTimeMinutes,
      siteTime: d.minutesActualDetail.siteTimeMinutes,
      webTime: d.minutesActualDetail.webTimeMinutes,
      actualTotalMinutes: d.minutesActualDetail.totalMinutes,
      expectedMinutes: d.minutesExpectedDetail.totalMinutes,
      expectedMinutesDetail: getToolTip(d.minutesExpectedDetail.driveTimeMinutes, d.minutesExpectedDetail.siteTimeMinutes, d.minutesExpectedDetail.webTimeMinutes),
      efficiencyPercent: formatDecimalPercent(d.efficiencyPercent),
      inspectedDate: d.inspectedDate,
      wasEdited: ([]),
      button: (
        <div className="edit-col">
          <IconButton
            onClick={() => handleEditRow(d.controlIdNumber)}
            disabled={payPeriodClosed}
            title="Edit"
            size="small"
          >
            <Icon>create</Icon>
          </IconButton>
        </div>
      )
    }));

    data.sort((d1, d2) => d1.efficiencyPercent - d2.efficiencyPercent);
    setInspectorPerformance(data);
  };

  const actions = [
    ...(performanceUpdates.length > 0 ? [{
      code: ACTION_SAVE,
      onClick: () => {
        setInspectorCaseTime(performanceUpdates.map(s =>
          ({ controlNumber: s.controlIdNumber, siteTime: s.siteTime, driveTime: s.driveTime, webTime: s.webTime })),
          () => {
            setPerformanceUpdates([]);
            setInspectorPerformance(null);
            getInspectorCaseTime(inspectorNumber, payPeriodId, transformAndSetCaseTime, mounted);
            makeToast("Save Successful", "success");
          }, mounted);
      },
    }] : []),
    {
      code: ACTION_DOWNLOAD,
    },
  ];
  const filteredPerformance = (inspectorPerformance || []).filter((row) => filterOnFilterDate(filterDate, row.inspectedDate));

  return (
    <>
      <MCard className="field-rep-performance" title="Performance">
        {inspectorPerformance && inspectorPerformance.length === 0 ?
          "No performance data found" :
          <MGrid
            headerText="FR Performance"
            onCellButtonClick={onCellClick}
            columnDefinitions={[
              {
                key: "controlIdNumber",
                name: "Control #",
                code: COL_CODE_CONTROL_NUMBER,
                dataType: DATA_TYPE_TEXT_BUTTON,
                dataSource: "controlIdNumber",
                filterType: FILTER_TYPE_NONE,
                tooltip: () => "Click to view case"
              },
              {
                key: "wasEditedIcon",
                name: (
                  <div title="Edited" style={{ textAlign: "center" }}>
                    <Icon>update</Icon>
                  </div>
                ),
                width: 40,
                dataSource: "wasEdited",
                filterType: FILTER_TYPE_NONE,
                tooltip: row => (row.wasEdited ? "Edited" : "")
              },
              {
                key: "surveyTypeName",
                name: "Survey Type",
                width: 275,
                dataSource: "surveyTypeName",
                filterType: FILTER_TYPE_NONE
              },
              {
                key: "driveMinutes",
                name: "Drive Mins",
                dataSource: "driveTime",
                filterType: FILTER_TYPE_NONE,
                dataType: DATA_TYPE_NUMBER,
                formatter: FORMATTER_RIGHT_ALIGN,
              },
              {
                key: "siteMinutes",
                name: "Site Mins",
                dataSource: "siteTime",
                filterType: FILTER_TYPE_NONE,
                dataType: DATA_TYPE_NUMBER,
                formatter: FORMATTER_RIGHT_ALIGN,
              },
              {
                key: "webMinutes",
                name: "Web Mins",
                dataSource: "webTime",
                filterType: FILTER_TYPE_NONE,
                dataType: DATA_TYPE_NUMBER,
                formatter: FORMATTER_RIGHT_ALIGN,
              },
              {
                key: "totalMinutes",
                name: "Total Mins",
                dataSource: "actualTotalMinutes",
                filterType: FILTER_TYPE_NONE,
                dataType: DATA_TYPE_NUMBER,
                formatter: FORMATTER_RIGHT_ALIGN,
              },
              {
                key: "expectedMinutes",
                name: "Exp. Mins",
                dataSource: "expectedMinutes",
                filterType: FILTER_TYPE_NONE,
                dataType: DATA_TYPE_NUMBER,
                tooltip: "expectedMinutesDetail",
                formatter: ExpectedMinutesFormatter,
              },
              {
                key: "percentage",
                name: "Eff. %",
                dataSource: "efficiencyPercent",
                filterType: FILTER_TYPE_NONE,
                dataType: DATA_TYPE_NUMBER,
                formatter: FORMATTER_RIGHT_ALIGN,
              },
              {
                key: "wasRejected",
                name: "R",
                width: 30,
                dataSource: "wasRejected",
                filterType: FILTER_TYPE_NONE,
                tooltip: (row) => (row.wasRejected === "R" ? "Rejected" : ""),
              },
              {
                key: "button",
                name: "",
                width: 70,
                dataSource: "button",
                filterType: FILTER_TYPE_NONE,
                tooltip: () => null
              },
              {
                key: "wasEdited",
                dataSource: "wasEdited",
                visible: false
              },
            ]}
            data={filteredPerformance}
            actions={actions}
          />}
      </MCard>
      {editDialogBox()}
    </>
  );
};

Performance.propTypes = {
  inspectorNumber: PropTypes.number.isRequired,
  payPeriodId: PropTypes.number.isRequired,
  payPeriodClosed: PropTypes.bool,
  makeToast: PropTypes.func,
  onControlNumberSelected: PropTypes.func,
  filterDate: PropTypes.instanceOf(Date),
};

export default Performance;
